import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AccountLookup from "common/accountLookup";
import StoreLookup from "common/storeLookup";
import InputField from "common/customInputField";
import SelectInput from "common/selectInput";
import Modal from "common/modal";
import edit from "svg/edit_blue.svg";
import Button from "react-bootstrap/Button";
import { address, nationalParent } from "../utils";
import { clearPrcList } from "store/slices/priceQuote";
import { prcService, salesRepService, getJobDetailsService } from "services/priceQuote.service";
import { useSelector, useDispatch } from "react-redux";
import { formatPhone } from "utils/common";
import { api } from "store/slices/api";
import { toast } from "react-toastify";
import "./style.scss";

const CustomerDetails = ({ territoryAccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const currentUser = useSelector((state) => state.loggedInUser);
  const { data: statesList } = api.useGetStatesQuery();

  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [showEditStoreModal, setShowEditStoreModal] = useState(false);
  const [isValidStoreLookup, setIsValidStoreLookup] = useState(true);
  const [isPhoneValid, setIsPhoneValid] = useState("");
  const [isZipValid, setIsZipValid] = useState("");
  const [isStorePhoneValid, setIsStorePhoneValid] = useState("");
  const [state, setState] = useState();
  const [terrValid, setTerrValid] = useState("");
  const [accountValid, setAccountValid] = useState(true);
  const { data: quoteFields = {}, isFetching: fetchingQuote } = api.useGetPriceQuoteQuery(quoteId);
  const [deprecatedEditQuote] = api.useDeprecatedEditQuoteMutation();

  useEffect(() => {
    if (fetchingQuote === false) {
      setState({ ...quoteFields });
    }
  }, [fetchingQuote]);

  useEffect(() => {
    if (showEditAccountModal) setState({ ...quoteFields });
  }, [showEditAccountModal]);

  useEffect(() => {
    if (showEditStoreModal) setState({ ...quoteFields });
  }, [showEditStoreModal]);

  const [defaultStoreData, setDefaultStoreData] = useState({});
  const detailsRef = useRef();

  useEffect(() => {
    setDefaultStoreData({
      storeNumber: state?.storeNumber,
      storeName: state?.storeName,
      storePhoneNumber: state?.storePhone,
    });
  }, [state]);

  const handleEditAccountInfo = () => {
    setShowEditAccountModal(true);
  };

  const handleEditStoreInfo = () => {
    setShowEditStoreModal(true);
  };

  useEffect(() => {
    dispatch(clearPrcList());
    if (state?.accountNumber && state.accountNumber.length === 9) {
      salesRepService(state.accountNumber, "1")
        .then((salesRes) => {
          if (salesRes && salesRes.terrCcn === "800100") {
            setState({ ...state, salesRep: `${currentUser.firstname} ${currentUser.lastname}`.trim() });
          } else if (salesRes) {
            setState({ ...state, salesRep: `${salesRes?.firstName ?? ""} ${salesRes?.lastName ?? ""}`.trim() });
          } else {
            toast.error("Sales Rep name could not be found");
          }
        })
        .catch((err) => {
          console.error(err);
          toast.error("Sales Rep name could not be found");
        });
    }
  }, [state?.accountNumber]);

  const resolveDefaultPricing = async (accountNumber) => {
    try {
      const jobs = await getJobDetailsService(accountNumber);
      if (!jobs?.length) {
        return { jobNbr: "", jobName: "", prcNbr: "", descr: "" };
      }
      const defaultJob = jobs.length > 1 ? jobs.reduce((a, b) => (b.jobNbr < a.jobNbr ? b : a)) : jobs[0];
      const { prcList } = await prcService({ acctNbr: accountNumber, jobNbr: defaultJob.jobNbr });
      if (!prcList?.length) {
        return { jobNbr: defaultJob.jobNbr, jobName: defaultJob.jobName, prcNbr: "", descr: "" };
      }
      const defaultPrc = prcList.length > 1 ? prcList.reduce((a, b) => (b.prcNbr < a.prcNbr ? b : a)) : prcList[0];
      return {
        jobNbr: defaultJob.jobNbr,
        jobName: defaultJob.jobName,
        prcNbr: defaultPrc.prcNbr,
        descr: defaultPrc.descr,
      };
    } catch (err) {
      console.error(err);
      return { jobNbr: "", jobName: "", prcNbr: "", descr: "" };
    }
  };

  const onAccountNumberChange = (accountNumber, isValid, data) => {
    setAccountValid(isValid);
    setState({
      ...state,
      accountNumber,
      accountName: data?.accountName,
      contactName: data?.contactName,
      phone: data?.phone,
      customerName: data?.customerName,
      parentNumber: data?.parentNumber,
      parentName: data?.parentName,
      execName: data?.execName,
      execNumber: data?.execNumber,
      city: data?.city,
      street: data?.street,
      zip: data?.zip,
      state: data?.state,
    });
  };

  const onAccountNameChange = (accountName, _isValid, data) => {
    if (data === null) {
      setState({ ...state, accountName });
      return;
    }

    setState({
      ...state,
      accountNumber: data?.accountNumber,
      accountName,
      contactName: data?.contactName,
      phone: data?.phone,
      customerName: data?.customerName,
      parentNumber: data?.parentNumber,
      parentName: data?.parentName,
      execName: data?.execName,
      execNumber: data?.execNumber,
      city: data?.city,
      street: data?.street,
      zip: data?.zip,
      state: data?.state,
    });
  };

  const onStoreNumberChange = (strNum, data) => {
    setState({ ...state, storeName: data.storeName, storeNumber: strNum, storePhoneNumber: data.storePhoneNumber });
  };

  const onStoreNameChange = (strName, isValid, data) => {
    setState({
      ...state,
      storeName: data.storeName,
      storeNumber: data.storeNumber,
      storePhoneNumber: data.storePhoneNumber,
    });
  };

  const onStoreLookupValidation = (isValid) => {
    if (isValid !== isValidStoreLookup) {
      setIsValidStoreLookup(isValid);
    }
  };

  const handleChange = (e) => {
    const key = e.target.id;
    const value = e.target.value;
    setState({ ...state, [key]: value });
  };

  const handleFieldsChange = (e, a) => {
    const { value, id } = e.target;
    setState({ ...state, [id]: a.id });
  };

  let title = "";
  if (showEditAccountModal) {
    title = "Edit Account Details";
  } else if (showEditStoreModal) {
    title = "Edit Store Information";
  }

  const handleCancel = () => {
    setShowEditAccountModal(false);
    setShowEditStoreModal(false);
  };

  const accountFormValidation = (phone, zip) => {
    let valid = true;
    var regEx = /^\d+$/;
    setIsPhoneValid("");
    setIsZipValid("");
    if (phone && (!phone.match(regEx) || phone.length !== 10)) {
      valid = false;
      setIsPhoneValid("Please enter a valid number");
    } else if (zip && !zip.match(regEx)) {
      valid = false;
      setIsZipValid("Please enter a valid zip");
    } else if (state.accountNumber !== null && state.accountNumber.length !== 0 && state.accountNumber.length !== 9) {
      return false;
    }
    return valid;
  };

  const storeFormValidation = (phone, terr) => {
    let valid = true;
    var regEx = /^\d+$/;
    setIsStorePhoneValid("");
    setTerrValid("");
    if (phone && (!phone.match(regEx) || phone.length !== 10)) {
      valid = false;
      setIsStorePhoneValid("Please enter a valid number");
    }
    if (!terr || terr === "") {
      valid = false;
      setTerrValid("Please enter Territory Number");
    } else if (terr && terr.length < 6) {
      valid = false;
      setTerrValid("Please enter 6 digits");
    }
    return valid;
  };

  const handleSave = () => {
    var isPrc = state.accountNumber !== quoteFields.accountNumber;
    var isStoreChanged = state.storeNumber !== quoteFields.storeNumber;
    const editPayload = (defaultPricing) => ({
      userName: currentUser.sub,
      quoteId,
      quoteName: !quoteFields.quoteName ? "" : quoteFields.quoteName,
      expirationDate: !quoteFields.expirationDate ? "" : quoteFields.expirationDate,
      territory: !state.territory ? "" : state.territory,
      salesRep: !state.salesRep ? "" : state.salesRep,
      accountNumber: !state.accountNumber ? "" : state.accountNumber,
      accountName: !state.accountName ? "" : state.accountName,
      contactName: !state.contactName ? "" : state.contactName,
      phoneNumber: !state.phone ? "" : state.phone,
      customerName: !state.customerName ? "" : state.customerName,
      storeNumber: !state.storeNumber ? "" : state.storeNumber,
      storeName: !state.storeName ? "" : state.storeName,
      storePhoneNumber: !state.storePhoneNumber ? "" : state.storePhoneNumber,
      parentNumber: !state.parentNumber ? "" : state.parentNumber,
      parentName: !state.parentName ? "" : state.parentName,
      execName: !state.execName ? "" : state.execName,
      execNumber: !state.execNumber ? "" : state.execNumber,
      taxRate: !quoteFields.taxRate ? "0.00" : quoteFields.taxRate,
      city: !state.city ? "" : state.city,
      street: !state.street ? "" : state.street,
      zip: !state.zip ? "" : state.zip,
      state: !state.state ? "" : state.state,
      jobNumber: isPrc ? defaultPricing.jobNbr : !quoteFields.jobNumber ? "" : quoteFields.jobNumber,
      jobName: isPrc ? defaultPricing.jobName : !quoteFields.jobName ? "" : quoteFields.jobName,
      prcNumber: isPrc ? defaultPricing.prcNbr : !quoteFields.prcNumber ? "" : quoteFields.prcNumber,
      prcDesc: isPrc ? defaultPricing.descr : !quoteFields.prcDesc ? "" : quoteFields.prcDesc,
      isTaxEdited: false,
      isPrcEdited: isPrc ? true : false,
      isStoreAccChanged: isPrc || isStoreChanged ? true : false,
    });
    if (showEditAccountModal) {
      if (!accountFormValidation(state.phone, state.zip)) {
        setShowEditAccountModal(true);
        return;
      }
      setShowEditAccountModal(false);
      const savePromise = (async () => {
        const defaultPricing = isPrc ? await resolveDefaultPricing(state.accountNumber) : null;
        return deprecatedEditQuote(editPayload(defaultPricing)).unwrap();
      })();
      toast.promise(savePromise, {
        pending: "Updating account details",
        success: "Account details updated successfully",
        error: "Failed to update account details",
      });
    } else if (showEditStoreModal) {
      if (!(storeFormValidation(state.storePhoneNumber, state.territory) && isValidStoreLookup)) {
        setShowEditStoreModal(true);
        return;
      }
      setShowEditStoreModal(false);
      toast.promise(deprecatedEditQuote(editPayload(null)).unwrap(), {
        pending: "Updating store information",
        success: "Store information updated successfully",
        error: "Failed to update store information",
      });
    }
  };

  const accountEditModal = () => {
    return (
      <>
        <div>
          <AccountLookup
            numberFieldChangeCallback={onAccountNumberChange}
            nameFieldChangeCallback={onAccountNameChange}
            nameFieldWrapperClass="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group"
            numberFieldWrapperClass="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group"
            initialAccountNumber={state.accountNumber}
            initialAccountName={state.accountName}
            isEdit={true}
          />

          <form className="search-form-container__fields__form">
            <div className="row">
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Customer Name"
                  id="customerName"
                  placeholder="Enter a name"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={state.customerName ?? ""}
                  onChange={handleChange}
                  isRequired={false}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Phone"
                  placeholder="999-999-9999"
                  id="phone"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={state.phone ?? ""}
                  onChange={handleChange}
                  isRequired={false}
                  errorMsg={isPhoneValid}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Contact Name"
                  placeholder="Enter a name"
                  id="contactName"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={state.contactName ?? ""}
                  onChange={handleChange}
                  isRequired={false}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  label="Sales Representative"
                  id="salesRep"
                  placeholder="Enter a name"
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={state.salesRep ?? ""}
                  onChange={handleChange}
                  isRequired={false}
                />
              </div>
            </div>
          </form>

          <div>
            <div className="customer-details__title">
              <span className="customer-details__title--text">Address</span>
            </div>
            <form className="search-form-container__fields__form">
              <div className="row">
                <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                  <InputField
                    type="text"
                    label="Street"
                    id="street"
                    placeholder="Enter a Street"
                    fieldClasses="form-control search-form-container__fields__form-group__item"
                    value={state.street ?? ""}
                    onChange={handleChange}
                    isRequired={false}
                  />
                </div>
                <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                  <InputField
                    type="text"
                    label="City"
                    placeholder="Enter a City"
                    id="city"
                    fieldClasses="form-control search-form-container__fields__form-group__item"
                    value={state.city ?? ""}
                    onChange={handleChange}
                    isRequired={false}
                  />
                </div>
                <div className="row">
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                    <SelectInput
                      options={statesList}
                      label="State"
                      defaultValue="Select a State"
                      value={state.state}
                      id="state"
                      handleSelectInput={(e, value) => {
                        handleFieldsChange(e, value);
                      }}
                    />
                  </div>
                  <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                    <InputField
                      type="text"
                      label="Zip"
                      placeholder="000000"
                      id="zip"
                      fieldClasses="form-control search-form-container__fields__form-group__item"
                      value={state.zip ?? ""}
                      onChange={handleChange}
                      isRequired={false}
                      errorMsg={isZipValid}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div>
            <div className="customer-details__title">
              <span className="customer-details__title--text">National Parent</span>
            </div>
            <form className="search-form-container__fields__form">
              <div className="row">
                <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                  <InputField
                    type="text"
                    label="Parent Number"
                    id="parentNumber"
                    placeholder="Enter a number"
                    fieldClasses="form-control search-form-container__fields__form-group__item"
                    value={state.parentNumber ?? ""}
                    onChange={handleChange}
                    isRequired={false}
                  />
                </div>
                <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                  <InputField
                    type="text"
                    label="Parent Name"
                    placeholder="Enter a name"
                    id="parentName"
                    fieldClasses="form-control search-form-container__fields__form-group__item"
                    value={state.parentName ?? ""}
                    onChange={handleChange}
                    isRequired={false}
                  />
                </div>
                <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                  <InputField
                    type="text"
                    label="NAE Number"
                    placeholder="Enter a number"
                    id="execNumber"
                    fieldClasses="form-control search-form-container__fields__form-group__item"
                    value={state.execNumber ?? ""}
                    onChange={handleChange}
                    isRequired={false}
                  />
                </div>
                <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                  <InputField
                    type="text"
                    label="NAE Name"
                    placeholder="Enter a name"
                    id="execName"
                    fieldClasses="form-control search-form-container__fields__form-group__item"
                    value={state.execName ?? ""}
                    onChange={handleChange}
                    isRequired={false}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  };

  const storeEditModal = () => {
    return (
      <div>
        <StoreLookup
          numberFieldChangeCallback={onStoreNumberChange}
          nameFieldChangeCallback={onStoreNameChange}
          onStoreLookupValidation={onStoreLookupValidation}
          debounceValue={1000}
          nameFieldWrapperClass="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group"
          numberFieldWrapperClass="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group"
          defaultData={defaultStoreData}
          isEdit={true}
        />

        <form className="search-form-container__fields__form">
          <div className="row">
            <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
              <InputField
                type="text"
                label="Phone"
                placeholder="999-999-9999"
                id="storePhoneNumber"
                fieldClasses="form-control search-form-container__fields__form-group__item"
                value={state.storePhoneNumber ?? ""}
                onChange={handleChange}
                isRequired={false}
                errorMsg={isStorePhoneValid}
              />
            </div>
            <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
              <InputField
                type="text"
                label="Territory number"
                id="territory"
                fieldClasses="form-control search-form-container__fields__form-group__item"
                value={state.territory ?? ""}
                onChange={handleChange}
                isRequired={false}
                errorMsg={terrValid}
              />
            </div>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="customer-details">
      <div className="d-flex customer-details__title justify-content-between mb-0">
        <div className="d-flex">
          <span className="customer-details__title--text">Account Details</span>
        </div>
        {territoryAccess && (
          <div className="d-flex">
            <img src={edit} alt="quote" />
            &nbsp;
            <span className="customer-details__title--edit-text cursor-style" onClick={handleEditAccountInfo}>
              Edit
            </span>
          </div>
        )}
      </div>

      <div className="d-md-flex flex-column flex-md-row mt-3 mt-md-5 overflow-auto position-relative" ref={detailsRef}>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Account Number</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.accountNumber ? "N/A" : quoteFields.accountNumber}
          </div>
        </div>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Account Name</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.accountName ? "N/A" : quoteFields.accountName}
          </div>
        </div>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Customer Name</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.customerName ? "N/A" : quoteFields.customerName}
          </div>
        </div>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">National Parent</div>
          <div className="info-item__value text-end text-md-start">
            {nationalParent(
              quoteFields.parentNumber,
              quoteFields.parentName,
              quoteFields.execNumber,
              quoteFields.execName,
            )}
          </div>
        </div>
      </div>

      <div
        className="d-md-flex flex-column flex-md-row mt-md-5 mb-5 overflow-auto position-relative spacing"
        ref={detailsRef}
      >
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Address</div>
          <div className="info-item__value text-end text-md-start">
            {!address(quoteFields.street, quoteFields.city, quoteFields.state, quoteFields.zip)
              ? "N/A"
              : address(quoteFields.street, quoteFields.city, quoteFields.state, quoteFields.zip)}
          </div>
        </div>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Contact Name</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.contactName ? "N/A" : quoteFields.contactName}
          </div>
        </div>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Phone</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.phone ? "N/A" : formatPhone(quoteFields.phone)}
          </div>
        </div>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Sales Representative</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.salesRep ? "N/A" : quoteFields.salesRep}
          </div>
        </div>
      </div>

      <div className="d-flex customer-details__title justify-content-between mb-0">
        <div className="d-flex">
          <span className="customer-details__title--text">Store Information</span>
        </div>
        {territoryAccess && (
          <div className="d-flex">
            <img src={edit} alt="quote" />
            &nbsp;
            <span className="customer-details__title--edit-text cursor-style" onClick={handleEditStoreInfo}>
              Edit
            </span>
          </div>
        )}
      </div>

      <div className="d-md-flex flex-column flex-md-row mt-3 mt-md-5 overflow-auto position-relative" ref={detailsRef}>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Store Number</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.storeNumber ? "N/A" : quoteFields.storeNumber}
          </div>
        </div>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Store Name</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.storeName ? "N/A" : quoteFields.storeName}
          </div>
        </div>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Phone</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.storePhoneNumber ? "N/A" : formatPhone(quoteFields.storePhoneNumber)}
          </div>
        </div>
        <div className="d-flex flex-row flex-md-column info-item justify-content-between justify-content-md-start col-12 col-md-3">
          <div className="info-item__label">Territory number</div>
          <div className="info-item__value text-end text-md-start">
            {!quoteFields.territory ? "N/A" : quoteFields.territory}
          </div>
        </div>
      </div>

      <div className="d-flex footer_display justify-content-between flex-column-reverse flex-md-row">
        <Button variant="outline-primary" onClick={() => navigate("/priceQuote")}>
          Back
        </Button>
        <Button className="m-0" variant="primary" onClick={() => navigate(`/priceQuote/${quoteId}#quote-details`)}>
          Next
        </Button>
      </div>

      {(showEditAccountModal || showEditStoreModal) && (
        <Modal
          title={title}
          saveButtonText={"Save"}
          closeButtonText={"Cancel"}
          handleSave={handleSave}
          handleCancel={handleCancel}
          size="lg"
          isSaveBtnDisabled={!accountValid}
        >
          {title === "Edit Account Details" ? accountEditModal() : storeEditModal()}
        </Modal>
      )}
    </div>
  );
};

export default CustomerDetails;
